"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";


const steps = [
{
  number: "01",
  title: "Start Free, Catalog Digitally",
  description:
  "Sign up free and catalog up to 5 items with photos, markers, and metadata. Build your digital preview and see how TapTale will transform your display — no credit card required, no commitment.",
  detail: "Free forever · 5 items · All viewer templates · Interactive markers",
  image: "https://images.unsplash.com/photo-1585151248595-875da5e6220f",
  imageAlt: "Person at desk cataloging collectibles on MacBook, smartphone nearby showing digital gallery preview, warm home office lighting, cozy collector's space",
  accent: "text-accent-crimson",
  glow: "bg-accent-crimson/10"
},
{
  number: "02",
  title: "Get Your Starter Kit",
  description:
  "Ready to bring your collection to life? Order the TapTale Starter Kit: 5 premium NFC tags plus your first month of service. Everything you need to transform your first shelf into an interactive exhibition.",
  detail: "$49 one-time · 5 NFC tags · First month included · Premium quality",
  image: "https://images.unsplash.com/photo-1596977764142-232b0ea515c9",
  imageAlt: "Premium NFC tag stickers on wooden surface, sleek minimal design, collectibles blurred in background, product photography style, studio lighting",
  accent: "text-accent-gold",
  glow: "bg-accent-gold/10"
},
{
  number: "03",
  title: "Tag, Tap, and Share",
  description:
  "Apply tags to your pieces, and watch your collection room transform. Visitors simply tap their phone to any tagged item — instant access to galleries, interactive markers, signatures, provenance, and the full story. Museum-quality experience, in your home.",
  detail: "Tap to reveal · Works with all phones · Update anytime · No app required",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10d000b2f-1771515383426.png",
  imageAlt: "Person holding iPhone near signed guitar with small NFC tag, TapTale viewer loading on screen with smooth animation, collection room background, dramatic lighting",
  accent: "text-accent-purple",
  glow: "bg-accent-purple/10"
}];


export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el, i) => {
              setTimeout(() => el.classList.add("revealed"), i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 px-6 md:px-8 bg-bg-secondary relative"
      aria-label="How TapTale works">
      
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
      <div className="max-w-container mx-auto relative z-10">
        {/* Header */}
        <div className="max-w-xl mb-20 reveal-hidden">
          <span className="font-mono text-[11px] text-accent-crimson uppercase tracking-[0.3em] block mb-4">
            How It Works
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[0.92] tracking-tight text-white">
            Three steps to
            <br />
            <span className="italic text-gray-400">bring it to life.</span>
          </h2>
        </div>

        {/* Steps */}
        <div className="space-y-6">
          {steps?.map((step, i) =>
          <div
            key={step?.number}
            className={`reveal-hidden stagger-${i + 1} group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center rounded-3xl border border-white/5 bg-bg-card p-6 lg:p-10 card-hover card-offset-shadow`}>
            
              {/* Text Side */}
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : "lg:order-1"}`}>
                {/* Step number */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`relative w-10 h-10 rounded-full ${step?.glow} border border-white/10 flex items-center justify-center`}>
                    <span className={`font-mono text-xs font-semibold ${step?.accent}`}>
                      {step?.number}
                    </span>
                    {i < steps?.length - 1 &&
                  <div className="step-connector" />
                  }
                  </div>
                  <div className="h-px flex-1 bg-white/5" />
                </div>

                <h3 className="font-serif text-2xl md:text-3xl font-light text-white mb-4 leading-tight">
                  {step?.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-5">
                  {step?.description}
                </p>
                <div className="flex items-center gap-2">
                  <span className={`w-1 h-1 rounded-full ${step?.glow} border border-current ${step?.accent}`} />
                  <span className={`font-mono text-[10px] uppercase tracking-widest ${step?.accent}`}>
                    {step?.detail}
                  </span>
                </div>
              </div>

              {/* Image Side */}
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : "lg:order-2"}`}>
                <div className="rounded-2xl overflow-hidden img-zoom h-56 md:h-72">
                  <AppImage
                  src={step?.image}
                  alt={step?.imageAlt}
                  width={700}
                  height={400}
                  className="w-full h-full object-cover" />
                
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}